import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaTimes, FaUpload, FaFile, FaFilePdf, FaFileWord, FaFileExcel, FaFileImage, FaFolder, FaTrash } from 'react-icons/fa';
import { ref, uploadBytes, getDownloadURL, listAll, deleteObject } from 'firebase/storage';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { storage, db } from '../../config/firebaseConfig';
import styles from './FileUploadModal.module.css';

const FileUploadModal = ({ onClose, project }) => {
  const [fileType, setFileType] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null);
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);
  
  const fileTypes = [
    { value: 'ilkBakanlikOnayi', label: 'İlk Bakanlık Onayı' },
    { value: 'ilkEtikKurulOnayi', label: 'İlk Etik Kurul Onayı' },
    { value: 'etikKurulSunumu', label: 'Etik Kurul Sunumu' },
    { value: 'etikKurulKarari', label: 'Etik Kurul Kararı' },
    { value: 'etikKurulOnayi', label: 'Etik Kurul Onayı' },
    { value: 'titck', label: 'TİTCK' },
    { value: 'diger', label: 'Diğer' },
  ];

  useEffect(() => {
    fetchUploadedFiles();
  }, [project.id]);

  // Handle closing when clicking outside the modal
  const handleOverlayClick = useCallback((e) => {
    if (e.target === modalRef.current && !isUploading) {
      onClose();
    }
  }, [onClose, isUploading]);

  // Add escape key listener to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !isUploading) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose, isUploading]);
  
  // Get file icon based on file type
  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    
    if (extension === 'pdf') return <FaFilePdf />;
    if (['doc', 'docx'].includes(extension)) return <FaFileWord />;
    if (['xls', 'xlsx', 'csv'].includes(extension)) return <FaFileExcel />;
    if (['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(extension)) return <FaFileImage />;
    return <FaFile />;
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const fetchUploadedFiles = async () => {
    try {
      // Get the project document to fetch existing files
      const projectDoc = await getDoc(doc(db, 'Projects', project.id));
      const projectData = projectDoc.data();
      
      if (projectData.files) {
        setUploadedFiles(projectData.files);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files.length > 0 && fileType) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files.length > 0 && fileType) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleFileUpload = async (file) => {
    if (!fileType) {
      alert('Lütfen bir dosya türü seçin');
      return;
    }

    setIsUploading(true);
    try {
      // Create a reference to the file in Firebase Storage
      const fileRef = ref(storage, `projects/${project.id}/${fileType}/${file.name}`);

      // Upload the file
      await uploadBytes(fileRef, file);

      // Get the download URL
      const downloadURL = await getDownloadURL(fileRef);

      // Update the project document in Firestore
      const projectRef = doc(db, 'Projects', project.id);
      const newFile = {
        type: fileType,
        name: file.name,
        url: downloadURL,
        uploadedAt: new Date().toISOString(),
        storagePath: `projects/${project.id}/${fileType}/${file.name}`
      };

      // Check if files array exists in the project
      const projectDoc = await getDoc(projectRef);
      const projectData = projectDoc.data();

      if (projectData.files) {
        await updateDoc(projectRef, {
          files: [...projectData.files, newFile]
        });
      } else {
        await updateDoc(projectRef, {
          files: [newFile]
        });
      }

      // Refresh the files list
      await fetchUploadedFiles();

    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Dosya yükleme hatası');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteFile = async (fileToDelete, index) => {
    if (!window.confirm(`"${fileToDelete.name}" dosyasını silmek istediğinizden emin misiniz?`)) {
      return;
    }

    setIsDeleting(index);
    try {
      // Delete from Firebase Storage
      const storagePath = fileToDelete.storagePath || `projects/${project.id}/${fileToDelete.type}/${fileToDelete.name}`;
      const fileRef = ref(storage, storagePath);
      await deleteObject(fileRef);

      // Update Firestore document
      const projectRef = doc(db, 'Projects', project.id);
      const projectDoc = await getDoc(projectRef);
      const projectData = projectDoc.data();

      if (projectData.files) {
        const updatedFiles = projectData.files.filter((file, idx) => {
          // Match by URL or name and type
          return file.url !== fileToDelete.url;
        });

        await updateDoc(projectRef, {
          files: updatedFiles
        });
      }

      // Refresh the files list
      await fetchUploadedFiles();
      alert('Dosya başarıyla silindi');
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Dosya silinirken hata oluştu: ' + error.message);
    } finally {
      setIsDeleting(null);
    }
  };

  return (
    <div 
      className={styles.modalOverlay}
      ref={modalRef}
      onClick={handleOverlayClick}
    >
      <div className={styles.modalContent}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Dosya Yükleme</h2>
          <button 
            onClick={onClose}
            className={styles.closeButton}
            disabled={isUploading}
            aria-label="Kapat"
          >
            <FaTimes />
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* File Type Dropdown */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              Dosya Türü:
            </label>
            <select 
              value={fileType}
              onChange={(e) => setFileType(e.target.value)}
              className={styles.formSelect}
              disabled={isUploading}
            >
              <option value="">Seçiniz</option>
              {fileTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* File Upload Area */}
          <div 
            className={styles.dropzone}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current.click()}
          >
            <div className={styles.dropzoneIcon}>
              <FaUpload />
            </div>
            <p className={styles.dropzoneText}>Dosyalarınızı Sürükleyin</p>
            <p className={styles.dropzoneText}>veya</p>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              className={styles.fileInput}
              disabled={isUploading}
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current.click();
              }}
              disabled={isUploading || !fileType}
              className={styles.uploadButton}
              type="button"
            >
              {isUploading ? (
                <>
                  <span className={styles.loadingSpinner}></span>
                  Yükleniyor...
                </>
              ) : (
                <>
                  <FaUpload />
                  Dosya Seçin
                </>
              )}
            </button>
          </div>

          {/* Uploaded Files List */}
          <div className={styles.fileListSection}>
            <h3 className={styles.fileListTitle}>
              <FaFolder style={{ marginRight: '0.5rem' }} />
              Yüklenmiş Dosyalar
            </h3>
            <div className={styles.fileList}>
              {uploadedFiles.length > 0 ? (
                uploadedFiles.map((file, index) => (
                  <div key={index} className={styles.fileItem}>
                    <div className={styles.fileType}>
                      {fileTypes.find(t => t.value === file.type)?.label || file.type}
                    </div>
                    <div
                      className={styles.fileLink}
                      onClick={() => window.open(file.url, '_blank')}
                    >
                      {getFileIcon(file.name)}
                      {file.name}
                    </div>
                    <div className={styles.fileActions}>
                      {file.uploadedAt && (
                        <small style={{ color: 'var(--file-type)', fontSize: '0.7rem' }}>
                          {formatDate(file.uploadedAt)}
                        </small>
                      )}
                      <button
                        className={styles.deleteButton}
                        onClick={() => handleDeleteFile(file, index)}
                        disabled={isDeleting === index || isUploading}
                        title="Dosyayı Sil"
                      >
                        {isDeleting === index ? (
                          <span className={styles.loadingSpinner}></span>
                        ) : (
                          <FaTrash />
                        )}
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.noFilesMessage}>
                  Henüz dosya yüklenmemiş
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileUploadModal;
